import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import BaseModal from "@restart/ui/Modal";
import { getSharedManager } from "react-bootstrap/BootstrapModalManager";
import { Spinner } from "react-bootstrap";
import ErrorBoundary from "../../Components/ErrorBoundary/ErrorBoundary";
import AlertSoundBanner from "../../Components/AlertSoundBanner/AlertSoundBanner";
import { CONNECTION_LOST_TEXT } from "../../Components/AlarmStatusBar/AlarmStatusBar";
import {
  ANNOUNCE_CLEAR_MS,
  LiveRegions,
  useAnnounce,
} from "../../Components/LiveAnnouncer/LiveAnnouncer";
import { useActivationGuard } from "../../Hooks/useActivationGuard";
import { getAlarmSnapshot, subscribeAlarms } from "../../Utils/alarmControl";
import CriticalAlertDetails from "./CriticalAlertDetails";
import {
  countLabel,
  criticalAlertName,
  describeAlertRemoval,
  describeCriticalAlert,
  getAlertActionLabel,
  getAlertActionLabels,
} from "./toastPresentation";
import "./criticalalertdialog.css";

export const NEW_ALERT_MARKER_MS = 5000;
export const SLOW_ACTION_MESSAGE =
  "Still waiting for the server. The alert stays open until it confirms.";

const ACTION_SELECTOR = "[data-critical-action]";
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const ACTIVATION_KEYS = ["Enter", " ", "Spacebar"];

const isActivationKey = (event) => ACTIVATION_KEYS.includes(event.key);

const ignoreRepeatedActivation = (event) => {
  if (event.repeat && isActivationKey(event)) event.preventDefault();
};

function escapeHint(userData) {
  const labels = getAlertActionLabels(userData);
  return labels.occupiedHidden
    ? `Use ${labels.responding} to handle this alert.`
    : `Use ${labels.responding} or ${labels.occupied} to handle this alert.`;
}

function otherAlertErrorText(alerts, userData) {
  if (alerts.length > 1) {
    return `Actions on ${alerts.length} other critical alerts were not recorded. Use Previous or Next to find them.`;
  }
  const [alert] = alerts;
  return `${getAlertActionLabel(alert.state.action, userData)} for ${criticalAlertName(alert.toast)} was not recorded. Use Previous or Next to find it.`;
}

function nextView(previous, queue, idsKey) {
  const previousIds = previous.queue.map((alert) => alert.id);
  const nextIds = queue.map((alert) => alert.id);
  const added = nextIds.filter((id) => !previousIds.includes(id));
  const seq = previous.transition.seq + 1;
  if (added.length) {
    return {
      idsKey,
      queue,
      activeId: nextIds[0] ?? null,
      transition: { seq, kind: "arrival", added: added.length },
    };
  }
  if (!nextIds.includes(previous.activeId)) {
    const previousIndex = Math.max(0, previousIds.indexOf(previous.activeId));
    return {
      idsKey,
      queue,
      activeId: nextIds[Math.min(previousIndex, nextIds.length - 1)] ?? null,
      transition: {
        seq,
        kind: "removed",
        removed:
          previous.queue.find((alert) => alert.id === previous.activeId) ??
          null,
      },
    };
  }
  return { ...previous, idsKey, queue };
}

function CriticalAlertCard({
  alert,
  userData,
  actionState,
  guard,
  onRespond,
  onOccupied,
  nameId,
  summaryId,
  errorId,
}) {
  const labels = getAlertActionLabels(userData);
  const status = actionState?.status;
  const pending = status === "pending";
  const inFlight = pending || status === "slow";
  const errorMessage = status === "error" ? actionState.message : "";
  const describedBy = errorMessage ? `${nameId} ${errorId}` : nameId;

  const activate = (handler) =>
    guard.guard((event) => {
      if (pending) {
        event?.preventDefault?.();
        return;
      }
      handler(alert.documentInfo, alert.id);
    });

  const actionProps = (action) => ({
    type: "button",
    "data-critical-action": action,
    "aria-describedby": describedBy,
    "aria-disabled": pending ? "true" : undefined,
    "aria-busy": inFlight && actionState.action === action ? "true" : undefined,
    onKeyDownCapture: guard.onKeyDownCapture,
    onKeyUpCapture: guard.onKeyUpCapture,
  });

  const spinnerFor = (action) =>
    inFlight && actionState.action === action ? (
      <Spinner
        as="span"
        animation="border"
        size="sm"
        aria-hidden="true"
        className="critical-alert-dialog-spinner"
      />
    ) : null;

  return (
    <>
      <div
        className="critical-alert-dialog-body"
        role="group"
        aria-labelledby={nameId}
      >
        <CriticalAlertDetails
          toast={alert}
          userData={userData}
          nameId={nameId}
          summaryId={summaryId}
        />
      </div>
      <div className="critical-alert-dialog-actions-area">
        <p role="status" className="critical-alert-dialog-waiting">
          {status === "slow" ? SLOW_ACTION_MESSAGE : ""}
        </p>
        <p id={errorId} role="alert" className="critical-alert-dialog-error">
          {errorMessage}
        </p>
        <div className="critical-toast-card-bottom-section critical-alert-dialog-actions">
          {!labels.occupiedHidden && (
            <button
              {...actionProps("Busy")}
              className="critical-toast-card-buttons critical-toast-card-occupied-button"
              onClick={activate(onOccupied)}
            >
              {spinnerFor("Busy")}
              {labels.occupied}
            </button>
          )}
          <button
            {...actionProps("Responded")}
            className="critical-toast-card-buttons critical-toast-card-responding-button"
            onClick={activate(onRespond)}
          >
            {spinnerFor("Responded")}
            {labels.responding}
          </button>
        </div>
      </div>
    </>
  );
}

function CriticalAlertDialog({
  alerts,
  userData,
  subjects,
  actionStates,
  resolvedRef,
  dismissedRef,
  onRespond,
  onOccupied,
  onDismissBroken,
  container,
  onHealthyChange,
  noticeArrivalCount = 0,
}) {
  const announce = useAnnounce();
  const alarms = useSyncExternalStore(subscribeAlarms, getAlarmSnapshot);
  const titleId = useId();
  const summaryId = useId();
  const nameId = useId();
  const errorId = useId();
  const modalRef = useRef(null);
  const headingRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const returnFocusRef = useRef(null);

  const queue = useMemo(() => [...alerts].reverse(), [alerts]);
  const idsKey = queue.map((alert) => alert.id).join("|");
  const [view, setView] = useState(() => ({
    idsKey,
    queue,
    activeId: queue[0]?.id ?? null,
    transition: { seq: 0, kind: "open" },
  }));
  let current = view;
  if (view.idsKey !== idsKey) {
    current = nextView(view, queue, idsKey);
    setView(current);
  }

  const total = queue.length;
  const activeIndex = Math.max(
    0,
    queue.findIndex((alert) => alert.id === current.activeId),
  );
  const active = queue[activeIndex] ?? null;
  const activeId = active?.id ?? null;
  const transition = current.transition;

  const actionGuard = useActivationGuard({ resetKey: activeId });

  const [showNewMarker, setShowNewMarker] = useState(false);
  const [escapePresses, setEscapePresses] = useState(0);

  const isTop = () => {
    const modal = modalRef.current;
    return !modal || modal.isTopModal();
  };

  const focusHeading = () => {
    if (!isTop()) return;
    headingRef.current?.focus();
  };

  const describeActive = () =>
    active ? describeCriticalAlert(active, userData) : "";

  useLayoutEffect(() => {
    const activeElement = document.activeElement;
    returnFocusRef.current =
      activeElement && activeElement !== document.body ? activeElement : null;
    onHealthyChange?.(true);
    return () => {
      onHealthyChange?.(false);
      const target = returnFocusRef.current;
      setTimeout(() => {
        const focused = document.activeElement;
        if (focused && focused !== document.body && focused.isConnected) {
          return;
        }
        const next = [
          target,
          document.querySelector(".toast-critical-realert-container"),
        ].find((element) => element?.isConnected && element.focus);
        next?.focus();
      }, 0);
    };
  }, []);

  useEffect(() => {
    const dialog = modalRef.current?.dialog;
    if (!dialog || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => {
      const focused = document.activeElement;
      if (focused && focused !== document.body) return;
      if (!modalRef.current?.isTopModal()) return;
      headingRef.current?.focus();
    });
    observer.observe(dialog, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (transition.seq === 0) return;
    if (transition.kind === "navigate") {
      announce(
        `Wellness Check Required (${activeIndex + 1} of ${total}): ${criticalAlertName(active)}.`,
      );
      return;
    }
    focusHeading();
    if (transition.kind === "arrival") {
      setShowNewMarker(true);
      const lead =
        transition.added > 1
          ? `${transition.added} new Wellness Checks Required. Showing`
          : "New Wellness Check Required:";
      announce(
        `${lead} ${describeActive()} Wellness Check Required (${activeIndex + 1} of ${total}).`,
        { politeness: "assertive" },
      );
      return;
    }
    if (transition.kind === "removed") {
      const removed = transition.removed;
      const removedMessage = describeAlertRemoval(removed, {
        resolvedAction: resolvedRef?.current?.get(removed?.id),
        dismissed: dismissedRef?.current?.has(removed?.id),
        subjects,
        userId: userData?.userInfo?.id,
        actionState: actionStates?.[removed?.id],
        userData,
      });
      const showing = active
        ? ` Showing Wellness Check Required (${activeIndex + 1} of ${total}): ${criticalAlertName(active)}.`
        : "";
      announce(`${removedMessage}${showing}`);
    }
  }, [transition.seq]);

  useEffect(() => {
    if (!showNewMarker) return undefined;
    const timer = setTimeout(
      () => setShowNewMarker(false),
      NEW_ALERT_MARKER_MS,
    );
    return () => clearTimeout(timer);
  }, [showNewMarker, transition.seq]);

  useEffect(() => {
    if (!escapePresses) return undefined;
    const timer = setTimeout(() => setEscapePresses(0), ANNOUNCE_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [escapePresses]);

  const previousQueueRef = useRef(queue);
  useEffect(() => {
    const previous = previousQueueRef.current;
    previousQueueRef.current = queue;
    const ids = new Set(queue.map((alert) => alert.id));
    previous.forEach((alert) => {
      if (ids.has(alert.id) || alert.id === transition.removed?.id) return;
      const action = resolvedRef?.current?.get(alert.id);
      if (!action) return;
      announce(
        `${getAlertActionLabel(action, userData)} recorded for ${criticalAlertName(alert)}.`,
      );
    });
  }, [idsKey]);

  const otherErrors = queue
    .filter(
      (alert) =>
        alert.id !== activeId && actionStates?.[alert.id]?.status === "error",
    )
    .map((alert) => ({ toast: alert, state: actionStates[alert.id] }));

  const handleShow = () => {
    focusHeading();
    if (noticeArrivalCount > 0) {
      announce(
        `${countLabel(noticeArrivalCount, "Wellness Check Required", "Wellness Checks Required")} arrived while the system use notice was open.`,
      );
    }
    const check = () => {
      const dialog = modalRef.current?.dialog;
      if (dialog && !dialog.contains(document.activeElement)) {
        announce(`Wellness Check Required: ${describeActive()}`, {
          politeness: "assertive",
        });
      }
    };
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(check);
    } else {
      setTimeout(check, 16);
    }
  };

  const navigate = (step) => {
    setView((previous) => {
      const ids = previous.queue.map((alert) => alert.id);
      if (ids.length < 2) return previous;
      const index = Math.max(0, ids.indexOf(previous.activeId));
      return {
        ...previous,
        activeId: ids[(index + step + ids.length) % ids.length],
        transition: { seq: previous.transition.seq + 1, kind: "navigate" },
      };
    });
  };

  const trapTab = (event) => {
    const dialog = event.currentTarget;
    const items = [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
      (element) =>
        !element.closest("[hidden]") &&
        element.getAttribute("aria-hidden") !== "true",
    );
    const focused = document.activeElement;
    if (!items.length) {
      event.preventDefault();
      headingRef.current?.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey) {
      if (
        focused === first ||
        focused === headingRef.current ||
        focused === dialog
      ) {
        event.preventDefault();
        last.focus();
      }
    } else if (focused === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape" || event.key === "Esc") {
      event.preventDefault();
      setEscapePresses((count) => count + 1);
      announce(escapeHint(userData));
    } else if (event.key === "Tab") {
      trapTab(event);
    }
    event.stopPropagation();
  };

  const handleFocus = (event) => {
    const dialog = event.currentTarget;
    if (event.target !== dialog) {
      lastFocusedRef.current = event.target;
      return;
    }
    const last = lastFocusedRef.current;
    const target =
      last &&
      last.isConnected &&
      dialog.contains(last) &&
      !last.matches(ACTION_SELECTOR)
        ? last
        : headingRef.current;
    target?.focus();
  };

  const content = (
    <>
      <div className="critical-alert-dialog-header">
        <div className="critical-alert-dialog-heading-row">
          <h2
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            className="critical-alert-dialog-title"
          >
            {total > 1
              ? `Wellness Check Required (${activeIndex + 1} of ${total})`
              : "Wellness Check Required"}
          </h2>
          {showNewMarker && (
            <span className="critical-alert-dialog-new" aria-hidden="true">
              New
            </span>
          )}
        </div>
        {total > 1 && (
          <div className="critical-alert-dialog-nav">
            <button
              type="button"
              className="critical-alert-dialog-nav-button"
              onKeyDown={ignoreRepeatedActivation}
              onClick={() => navigate(-1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="critical-alert-dialog-nav-button"
              onKeyDown={ignoreRepeatedActivation}
              onClick={() => navigate(1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
      {otherErrors.length > 0 && (
        <p role="alert" className="critical-alert-dialog-other-error">
          {otherAlertErrorText(otherErrors, userData)}
        </p>
      )}
      {active && (
        <ErrorBoundary
          key={active.id}
          variant="item"
          label="A Wellness Check Required"
          className="critical-alert-dialog-item-fallback"
          onDismiss={() => onDismissBroken(active.id)}
        >
          <CriticalAlertCard
            alert={active}
            userData={userData}
            actionState={actionStates?.[active.id]}
            guard={actionGuard}
            onRespond={onRespond}
            onOccupied={onOccupied}
            nameId={nameId}
            summaryId={summaryId}
            errorId={errorId}
          />
        </ErrorBoundary>
      )}
      <div className="critical-alert-dialog-footer">
        {alarms.connection.lost && (
          <p className="critical-alert-dialog-connection">
            {CONNECTION_LOST_TEXT}
          </p>
        )}
        <ErrorBoundary variant="silent">
          <AlertSoundBanner />
        </ErrorBoundary>
        {escapePresses > 0 && (
          <p className="critical-alert-dialog-hint" aria-hidden="true">
            {escapeHint(userData)}
          </p>
        )}
        <LiveRegions />
      </div>
    </>
  );

  return (
    <BaseModal
      ref={modalRef}
      show
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={summaryId}
      className="critical-alert-dialog"
      backdrop={false}
      keyboard={false}
      enforceFocus
      restoreFocus={false}
      manager={getSharedManager()}
      container={container}
      onShow={handleShow}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      renderDialog={(dialogProps) => <div {...dialogProps}>{content}</div>}
    />
  );
}

export default CriticalAlertDialog;
